import { useMemo, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { APPOINTMENT_SLOT_STARTS, type Appointment } from '@tailoring-crm/shared';
import { formatDate } from '../../lib/format.js';
import { dayKeyOf, slotKeyOf, slotSpan } from '../../lib/appointments/week.js';
import { cn } from '@tailoring-crm/ui';
import { AppointmentCell } from './appointment-cell.js';

/**
 * The week: half-hour rows down the side, Monday to Sunday across the top.
 *
 * EVERYTHING SIZED BY THE SHARED CONSTANTS. The rows come from `APPOINTMENT_SLOT_STARTS`; how
 * many of them one booking covers comes from `slotSpan`, which is built on
 * `APPOINTMENT_SLOT_MINUTES`. Nothing here counts thirty minutes or twenty-four rows by hand, so
 * a change to the shared shop-hours contract changes this screen with it rather than beside it.
 *
 * A CELL A LATER ROW ALREADY COVERS RENDERS NOTHING: a ninety-minute fitting starting at ten is
 * one `<td>` with `rowSpan={3}`, and the two rows below it must not draw a `<td>` of their own or
 * the table would gain a column partway down. Two bookings that happen to start in the exact
 * same cell — different staff, the same slot — share that one cell rather than fighting the
 * table for a column each; a true side-by-side lane view is more than a phone-width grid can
 * offer and is not what this task asks for.
 */

const WEEKDAY_KEYS = ['0', '1', '2', '3', '4', '5', '6'] as const;

interface PlacedAppointment {
  appointment: Appointment;
  span: number;
}

export function AppointmentWeekGrid({
  weekDays,
  items,
  onSlotClick,
  onReschedule,
  onCancel,
  onMarkComplete,
}: {
  /** The seven `'YYYY-MM-DD'` day keys, Monday first. */
  weekDays: readonly string[];
  items: readonly Appointment[];
  /** An empty cell was tapped: book here. */
  onSlotClick: (dayKey: string, startTime: string) => void;
  onReschedule: (appointment: Appointment) => void;
  onCancel: (appointment: Appointment) => void;
  onMarkComplete: (appointment: Appointment) => void;
}): ReactElement {
  const { t } = useTranslation();
  const todayKey = useMemo(() => dayKeyOf(Date.now()), []);

  const { bySlot, covered, outside } = useMemo(() => {
    const bySlotMap = new Map<string, Map<number, PlacedAppointment[]>>();
    const coveredMap = new Map<string, Set<number>>();
    const outsideList: Appointment[] = [];

    for (const day of weekDays) {
      bySlotMap.set(day, new Map());
      coveredMap.set(day, new Set());
    }

    for (const appointment of items) {
      const day = dayKeyOf(appointment.startAt);
      if (!bySlotMap.has(day)) continue; // Not in this week at all — cannot happen, kept defensive.

      const slotKey = slotKeyOf(appointment.startAt);
      const rowIndex = APPOINTMENT_SLOT_STARTS.indexOf(slotKey);
      if (rowIndex === -1) {
        outsideList.push(appointment);
        continue;
      }

      const rawSpan = slotSpan(appointment.durationMinutes);
      const span = Math.min(rawSpan, APPOINTMENT_SLOT_STARTS.length - rowIndex);

      const dayMap = bySlotMap.get(day);
      const existing = dayMap?.get(rowIndex) ?? [];
      dayMap?.set(rowIndex, [...existing, { appointment, span }]);

      const daySet = coveredMap.get(day);
      for (let offset = 1; offset < span; offset += 1) {
        daySet?.add(rowIndex + offset);
      }
    }

    outsideList.sort((a, b) => a.startAt.localeCompare(b.startAt));
    return { bySlot: bySlotMap, covered: coveredMap, outside: outsideList };
  }, [weekDays, items]);

  return (
    <div className="flex flex-col gap-4">
      <div className="overflow-x-auto rounded-lg border border-border">
        <table className="w-full min-w-[720px] border-collapse text-sm">
          <thead>
            <tr>
              <td className="w-20 border-b border-border p-2" />
              {weekDays.map((day, index) => (
                <th
                  key={day}
                  scope="col"
                  className={cn(
                    'border-b border-l border-border p-2 text-center align-top font-medium',
                    day === todayKey && 'bg-primary/10',
                  )}
                >
                  <div className="flex flex-col items-center gap-0.5">
                    <span className="flex items-center gap-1">
                      {t(`appointments.weekday.${WEEKDAY_KEYS[index]}`)}
                      {day === todayKey ? (
                        <span className="rounded-full bg-primary px-1.5 py-0.5 text-[10px] font-semibold text-primary-foreground">
                          {t('appointments.today')}
                        </span>
                      ) : null}
                    </span>
                    <span className="text-xs font-normal text-muted-foreground">
                      {formatDate(day)}
                    </span>
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {APPOINTMENT_SLOT_STARTS.map((startTime, rowIndex) => (
              <tr key={startTime}>
                <th
                  scope="row"
                  className="border-b border-border p-1.5 text-right align-top text-xs font-normal text-muted-foreground"
                >
                  {startTime}
                </th>
                {weekDays.map((day) => {
                  if (covered.get(day)?.has(rowIndex)) return null;

                  const placed = bySlot.get(day)?.get(rowIndex) ?? [];
                  if (placed.length > 0) {
                    const rowSpan = Math.max(...placed.map((one) => one.span));
                    return (
                      <td
                        key={day}
                        rowSpan={rowSpan}
                        className={cn(
                          'border-b border-l border-border p-1 align-top',
                          day === todayKey && 'bg-primary/5',
                        )}
                      >
                        <div className="flex flex-col gap-1">
                          {placed.map(({ appointment }) => (
                            <AppointmentCell
                              key={appointment.id}
                              appointment={appointment}
                              onReschedule={onReschedule}
                              onCancel={onCancel}
                              onMarkComplete={onMarkComplete}
                            />
                          ))}
                        </div>
                      </td>
                    );
                  }

                  return (
                    <td
                      key={day}
                      className={cn(
                        'border-b border-l border-border p-0 align-top',
                        day === todayKey && 'bg-primary/5',
                      )}
                    >
                      <button
                        type="button"
                        className="h-full min-h-[2.5rem] w-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset hover:bg-accent"
                        onClick={() => onSlotClick(day, startTime)}
                        aria-label={t('appointments.grid.bookSlot', {
                          date: formatDate(day),
                          time: startTime,
                        })}
                      />
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {outside.length > 0 ? (
        <div className="flex flex-col gap-2 rounded-lg border border-dashed border-border p-3">
          <div>
            <p className="text-sm font-semibold">{t('appointments.outsideHours.title')}</p>
            <p className="text-sm text-muted-foreground">
              {t('appointments.outsideHours.description')}
            </p>
          </div>
          <ul className="flex flex-col gap-2">
            {outside.map((appointment) => (
              <li key={appointment.id} data-testid="appointment-outside-hours">
                <AppointmentCell
                  appointment={appointment}
                  onReschedule={onReschedule}
                  onCancel={onCancel}
                  onMarkComplete={onMarkComplete}
                />
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </div>
  );
}

export default AppointmentWeekGrid;
